/**
 * urgentScreening.js — R20 · PROD-163 (Dr. Rami 2026-10-03), patient P1 step 1.
 * The emergency screening (Emergency.jsx) with every answer change, in the shape the API stores.
 *
 * ★★★ THE DEFECT. Emergency.jsx kept the three answers in component state and, on "No — let's
 *   begin", wrote a fixed { heart: 'no', stroke: 'no', crisis: 'no', timestamp } into the LOCAL
 *   store only. Its header comment said it "persists answers on case"; casesAPI.create sent
 *   specialty and focus area and nothing else. A patient who tapped Yes and then No left no trace
 *   anywhere the physician could see.
 *
 * ★★ THE CONTRACT (orella-api D5b step 4, 2d79b54, create_case `urgentScreening`, validated by
 *   validate_urgent_screening, exact keys, nothing else):
 *     { answers:     { heart, stroke, crisis: 'yes' | 'no' },   the final answers
 *       history:     [{ question, answer, at }, ...],            EVERY change, in order
 *       submittedAt: ISO-8601 with an offset }
 *   History is chronological, its last answer per question equals the final answer, at most 60
 *   items and 8000 characters as JSON. A payload that breaks any rule is REFUSED by the server
 *   (the case is still created, without it), so this file builds it to the rule.
 *   The server stores it once and never lets the patient change it (update_case drops it).
 *
 * ★ A CHANGE IS A CHANGE: tapping the answer that is already selected records nothing.
 * ★ ORDER OVER CLOCK: a tap time earlier than the one before it (the device clock stepped back)
 *   is clamped up to it, so the history is always chronological as the server requires.
 * ★ Over the cap (a patient toggling sixty times) the history keeps, in order: each question's
 *   first 'yes', each question's last entry, and then the most recent others. The 'yes' the
 *   physician must see and the agreement with the final answers both survive.
 *
 * Pure (no imports). scripts/check-emergency-screening.mjs executes it.
 */
export const SCREENING_QUESTIONS = Object.freeze(['heart', 'stroke', 'crisis'])
export const SCREENING_HISTORY_MAX = 60

const isAnswer = (v) => v === 'yes' || v === 'no'

// ★ THE CLOCK MAY STEP BACK (a device time change, an NTP correction between two taps). The
//   server refuses a history that is not chronological ('history_order') and then drops the WHOLE
//   screening, so a time earlier than the one before it is clamped up to it: the order of the
//   taps is the truth, the clock only labels it.
const ms = (s) => { const t = Date.parse(s); return Number.isNaN(t) ? null : t }
function monotonic(list) {
  let prevAt = null, prevMs = null
  return list.map((h) => {
    const t = ms(h.at)
    if (prevMs !== null && (t === null || t < prevMs)) return { ...h, at: prevAt }
    if (t !== null) { prevAt = h.at; prevMs = t }
    return h
  })
}

/** History after `question` changed to `answer` at `at` (ISO). Unchanged when nothing changed. */
export function recordAnswerChange(history, current, question, answer, at) {
  const list = Array.isArray(history) ? history : []
  if (!SCREENING_QUESTIONS.includes(question) || !isAnswer(answer)) return list
  if (current && current[question] === answer) return list
  return monotonic([...list, { question, answer, at: String(at || new Date().toISOString()) }])
}

function capped(history) {
  if (history.length <= SCREENING_HISTORY_MAX) return history
  const keep = new Set()
  for (const q of SCREENING_QUESTIONS) {
    const firstYes = history.findIndex((h) => h.question === q && h.answer === 'yes')
    if (firstYes >= 0) keep.add(firstYes)
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].question === q) { keep.add(i); break }
    }
  }
  for (let i = history.length - 1; i >= 0 && keep.size < SCREENING_HISTORY_MAX; i--) keep.add(i)
  return history.filter((_, i) => keep.has(i))
}

/**
 * The create_case `urgentScreening` payload, or null when the answers are not complete.
 * `answers` is { heart, stroke, crisis } ('yes' | 'no'); `history` what recordAnswerChange built.
 */
export function buildUrgentScreening(answers, history, submittedAt) {
  const a = answers || {}
  if (!SCREENING_QUESTIONS.every((q) => isAnswer(a[q]))) return null
  const clean = (Array.isArray(history) ? history : [])
    .filter((h) => h && SCREENING_QUESTIONS.includes(h.question) && isAnswer(h.answer) && typeof h.at === 'string')
    .map((h) => ({ question: h.question, answer: h.answer, at: h.at }))
  return {
    answers: { heart: a.heart, stroke: a.stroke, crisis: a.crisis },
    history: capped(monotonic(clean)),
    submittedAt: String(submittedAt || new Date().toISOString()),
  }
}

/**
 * What a returning patient sees: { answers, history } from a screening stored on the case, in
 * this shape or in the old local one ({ heart, stroke, crisis, timestamp }, no history).
 */
export function restoreScreening(stored) {
  const s = stored && typeof stored === 'object' ? stored : null
  const src = s && s.answers && typeof s.answers === 'object' ? s.answers : s
  const answers = { heart: null, stroke: null, crisis: null }
  if (src) for (const q of SCREENING_QUESTIONS) if (isAnswer(src[q])) answers[q] = src[q]
  const history = s && Array.isArray(s.history)
    ? s.history.filter((h) => h && SCREENING_QUESTIONS.includes(h.question) && isAnswer(h.answer) && typeof h.at === 'string')
    : []
  return { answers, history }
}
